import React, { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import AuditTargetSelect from './AuditTargetSelect';
import { sandboxService } from '../../services/sandboxService';
import { schwachstellenService } from '../../services/schwachstellenService';
import { SchwachstelleList, TransferTargetAudit } from '../../types';
import { extractApiError } from '../../utils/errorUtils';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

/** Audit oder einzelnes Finding zum Lesen in die isolierte Umgebung freigeben. */
const FreigabeAddDialog: React.FC<Props> = ({ open, onClose, onCreated }) => {
  const [typ, setTyp] = useState<'audit' | 'schwachstelle'>('audit');
  const [audit, setAudit] = useState<TransferTargetAudit | null>(null);
  const [findings, setFindings] = useState<SchwachstelleList[]>([]);
  const [finding, setFinding] = useState<SchwachstelleList | null>(null);
  const [loadingFindings, setLoadingFindings] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setTyp('audit');
      setAudit(null);
      setFindings([]);
      setFinding(null);
      setError(null);
    }
  }, [open]);

  useEffect(() => {
    setFinding(null);
    if (typ !== 'schwachstelle' || !audit) {
      setFindings([]);
      return;
    }
    let cancelled = false;
    setLoadingFindings(true);
    schwachstellenService
      .list({ audit_id: audit.id })
      .then((rows) => !cancelled && setFindings(rows))
      .catch(() => !cancelled && setFindings([]))
      .finally(() => !cancelled && setLoadingFindings(false));
    return () => {
      cancelled = true;
    };
  }, [typ, audit]);

  const canSave = typ === 'audit' ? !!audit : !!finding;

  const handleConfirm = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      if (typ === 'audit' && audit) await sandboxService.freigaben.createForAudit(audit.id);
      if (typ === 'schwachstelle' && finding) await sandboxService.freigaben.createForFinding(finding.id);
      window.dispatchEvent(new Event('sandbox-freigaben-changed'));
      onCreated();
    } catch (e) {
      setError(extractApiError(e, 'Freigabe fehlgeschlagen'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Freigabe hinzufügen</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Das freigegebene Audit bzw. Finding ist in Ihrer isolierten Umgebung ausschließlich lesbar. Es werden keine
          Kunden-, Projekt- oder Personendaten preisgegeben.
        </Typography>
        <RadioGroup row value={typ} onChange={(e) => setTyp(e.target.value as 'audit' | 'schwachstelle')} sx={{ mb: 1 }}>
          <FormControlLabel value="audit" control={<Radio size="small" />} label="Ganzes Audit" />
          <FormControlLabel value="schwachstelle" control={<Radio size="small" />} label="Einzelnes Finding" />
        </RadioGroup>
        <AuditTargetSelect active={open} onChange={(a) => setAudit(a)} />
        {typ === 'schwachstelle' && (
          <Autocomplete
            options={findings}
            value={finding}
            loading={loadingFindings}
            onChange={(_e, v) => setFinding(v)}
            getOptionLabel={(o) => o.titel}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            disabled={!audit}
            noOptionsText={audit ? 'Keine Findings in diesem Audit' : 'Zuerst Audit wählen'}
            renderInput={(params) => <TextField {...params} label="Finding" size="small" data-testid="freigabe-finding-select" />}
            sx={{ mt: 2 }}
          />
        )}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={{ color: 'text.secondary' }}>Abbrechen</Button>
        <Button onClick={() => void handleConfirm()} variant="contained" disabled={!canSave || saving} data-testid="freigabe-confirm">
          {saving ? 'Freigeben…' : 'Freigeben'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FreigabeAddDialog;
